(function () {
  'use strict';
  if (customElements.get('mm-configurator')) return;
  var instance = 0;

  class MMConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.uid = 'mm-configurator-' + (++instance);
      this.model = window.MMConfiguratorModel;
      this.state = ['', '', ''];
      this.live = null;
      this.catalog = null;
      this.media = {};
      this.epoch = (this.epoch || 0) + 1;
      this.galleryMode = false;
      this.submitting = false;
      this.defaultAlt = this.find('[data-mm-preview-image]')?.alt || 'Product gallery photograph';
      var signal = this.lifecycle.signal;
      this.addEventListener('change', event => this.onChange(event), { signal });
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.find('[data-mm-form]')?.addEventListener('submit', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.addToCart();
      }, { capture: true, signal });
      this.find('[data-mm-preview-image]')?.addEventListener('error', () => {
        if (this.galleryMode || this.find('[data-mm-preview-image]')?.hidden || !this.model?.previewKey(this.state)) return;
        if (!this.renderDigitalPreview()) this.pendingPreview();
      }, { signal });
      window.addEventListener('popstate', () => {
        if (this.catalog && !this.submitting) this.readURL();
      }, { signal });
      window.addEventListener('pageshow', event => {
        if (event.persisted && this.catalog && this.submitting) {
          this.submitting = false;
          this.selectionChanged(false);
        }
      }, { signal });
      this.initialize();
    }

    disconnectedCallback() {
      this.epoch += 1;
      this.lifecycle?.abort();
      this.liveRequest?.abort();
      this.lifecycle = null;
    }

    find(selector) { return this.querySelector(selector); }
    text(selector, value) { var el = this.find(selector); if (el) el.textContent = value; }
    hide(selector, hidden) { var el = this.find(selector); if (el) el.hidden = hidden; }
    error(message) {
      this.text('[data-mm-error]', message);
      this.hide('[data-mm-error]', !message);
    }

    safeURL(value, assetsOnly) {
      if (!value || typeof value !== 'string') return null;
      try {
        var url = new URL(value, window.location.href);
        if (!['http:', 'https:'].includes(url.protocol)) return null;
        if (url.origin === window.location.origin) return url.href;
        if (url.protocol === 'https:' && (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com'))) return url.href;
        return null;
      } catch (_) { return null; }
    }

    async jsonAsset(value) {
      var url = this.safeURL(value, true);
      if (!url) throw new Error('Invalid catalog asset URL');
      var result = await fetch(url, { credentials: 'same-origin', signal: this.lifecycle.signal });
      if (!result.ok) throw new Error('Asset could not be loaded');
      return result.json();
    }

    async initialize() {
      var epoch = ++this.epoch;
      this.error('');
      this.hide('[data-mm-retry]', true);
      this.loading = true;
      this.render();
      try {
        if (!this.model) throw new Error('Selection library is unavailable');
        var results = await Promise.all([
          this.jsonAsset(this.dataset.catalogUrl),
          this.dataset.mediaUrl ? this.jsonAsset(this.dataset.mediaUrl).catch(() => ({})) : Promise.resolve({})
        ]);
        if (!this.isConnected || epoch !== this.epoch) return;
        if (String(results[0].productId) !== this.dataset.productId) throw new Error('Catalog product does not match');
        this.catalog = this.model.buildCatalog(results[0]);
        if (!this.catalog.variants.length) throw new Error('Catalog is empty');
        this.media = results[1] || {};
        this.loading = false;
        this.buildChoices();
        this.readURL();
      } catch (error) {
        if (!this.isConnected || epoch !== this.epoch) return;
        this.loading = false;
        this.catalog = null;
        this.error('Design options could not be loaded. Please retry.');
        this.hide('[data-mm-retry]', false);
        this.render();
      }
    }

    buildChoices() {
      this.catalog.options.forEach((option, index) => {
        var container = this.find('[data-mm-step="' + index + '"] [data-mm-choices]');
        if (!container) return;
        container.replaceChildren();
        option.values.forEach((value, position) => {
          var label = document.createElement('label');
          label.className = 'mm-choice';
          label.dataset.mmChoice = '';
          label.title = value;
          var radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = this.uid + '-option-' + index;
          radio.id = this.uid + '-' + index + '-' + position;
          radio.value = value;
          radio.dataset.mmIndex = String(index);
          radio.setAttribute('aria-label', value);
          label.htmlFor = radio.id;
          label.append(radio);
          var icon = index === 0 ? this.safeURL(this.media.patternIcons?.[value], true) : null;
          var name = document.createElement('span');
          name.className = 'mm-choice__label';
          name.textContent = value;
          if (icon) {
            label.classList.add('mm-choice--icon');
            var image = document.createElement('img');
            image.className = 'mm-choice__icon';
            image.alt = '';
            image.loading = 'lazy';
            image.src = icon;
            image.addEventListener('error', () => { image.remove(); name.hidden = false; });
            label.append(image);
            name.hidden = true;
          } else if (index > 0) {
            var color = (index === 1 ? this.media.backgroundSwatches : this.media.frameSwatches)?.[value];
            label.classList.add(index === 1 ? 'mm-choice--background' : 'mm-choice--frame');
            if (typeof color === 'string' && /^#(?:[a-f\d]{3}|[a-f\d]{6})$/i.test(color)) {
              label.style.setProperty('--mm-choice-color', color);
              if (index === 1) {
                var hex = color.slice(1);
                if (hex.length === 3) hex = hex.split('').map(character => character + character).join('');
                var red = parseInt(hex.slice(0, 2), 16);
                var green = parseInt(hex.slice(2, 4), 16);
                var blue = parseInt(hex.slice(4, 6), 16);
                label.style.setProperty('--mm-choice-text', ((red * 299 + green * 587 + blue * 114) / 1000) < 145 ? '#ffffff' : '#292822');
              }
            }
            if (index === 2) {
              var texture = this.safeURL(this.media.frameTextures?.[value], true);
              if (texture) label.style.setProperty('--mm-frame-texture', 'url("' + texture + '")');
            }
          }
          label.append(name);
          container.append(label);
        });
      });
    }

    onChange(event) {
      var radio = event.target.closest('input[data-mm-index]');
      if (!radio || !this.contains(radio) || !this.catalog) return;
      if (this.submitting) { this.render(); return; }
      var next = this.model.select(this.catalog, this.state, Number(radio.dataset.mmIndex), radio.value);
      if (JSON.stringify(next) === JSON.stringify(this.state)) { this.render(); return; }
      this.state = next;
      this.selectionChanged(true);
    }

    // The frame is the only optional choice - previewKey happily renders a
    // pattern-and-background design without it - so clicking the swatch that is
    // already selected clears it and falls back to that two-choice preview.
    clearOptionalIfReselected(event) {
      var radio = event.target.closest('input[data-mm-index]');
      if (!radio || !this.catalog) return false;
      var index = Number(radio.dataset.mmIndex);
      if (index !== this.catalog.options.length - 1) return false;
      if (this.state[index] !== radio.value) return false;
      var next = this.model.select(this.catalog, this.state, index, '');
      if (next[index]) return false;
      radio.checked = false;
      this.state = next;
      this.selectionChanged(true);
      return true;
    }

    onClick(event) {
      var choice = event.target.closest('[data-mm-choice]');
      if (choice && this.contains(choice) && !this.submitting && !choice.closest('fieldset')?.disabled) {
        this.galleryMode = false;
        if (this.clearOptionalIfReselected(event)) return;
        this.renderPreview();
      }
      var button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-mm-retry')) {
        event.preventDefault();
        if (this.catalog) this.selectionChanged(false); else this.initialize();
      } else if (button.hasAttribute('data-mm-gallery-src')) {
        event.preventDefault();
        var url = this.safeURL(button.dataset.mmGallerySrc, true);
        if (!url) return;
        this.galleryMode = true;
        this.setImage(url, button.dataset.mmGalleryAlt || this.defaultAlt);
        this.text('[data-mm-preview-note]', 'Product gallery photograph');
        this.text('[data-mm-preview-title]', 'Gallery');
        this.hide('[data-mm-return-preview]', false);
      } else if (button.hasAttribute('data-mm-return-preview')) {
        event.preventDefault();
        this.galleryMode = false;
        this.renderPreview();
      }
    }

    readURL() {
      var id = new URL(window.location.href).searchParams.get('variant');
      var variant = this.catalog.byId.get(id);
      this.state = variant ? variant.options.slice() : this.model.emptyState();
      this.selectionChanged(false);
    }

    selectionChanged(updateURL) {
      this.epoch += 1;
      this.liveRequest?.abort();
      this.live = null;
      this.loading = false;
      this.galleryMode = false;
      this.error('');
      this.hide('[data-mm-success]', true);
      this.hide('[data-mm-retry]', true);
      var variant = this.model.exactVariant(this.catalog, this.state);
      if (updateURL) {
        var url = new URL(window.location.href);
        if (variant) url.searchParams.set('variant', variant.id); else url.searchParams.delete('variant');
        window.history.replaceState(window.history.state, '', url);
      }
      this.render();
      this.renderPreview();
      if (variant) this.validateVariant(variant);
    }

    async validateVariant(variant) {
      var epoch = ++this.epoch;
      this.liveRequest?.abort();
      this.liveRequest = new AbortController();
      this.loading = true;
      this.render();
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('section_id', this.dataset.sectionId);
        var result = await fetch(url.href, { credentials: 'same-origin', signal: this.liveRequest.signal });
        if (!result.ok) throw new Error('Live variant request failed');
        var html = new DOMParser().parseFromString(await result.text(), 'text/html');
        var node = html.querySelector('[data-mm-live-variant]');
        var live = node ? JSON.parse(node.textContent) : null;
        if (!this.isConnected || epoch !== this.epoch) return;
        if (!live || String(live.productId) !== this.dataset.productId || String(live.id) !== variant.id ||
            JSON.stringify(live.options) !== JSON.stringify(variant.options) || typeof live.available !== 'boolean' ||
            !Number.isFinite(live.price) || live.price < 0 || typeof live.money !== 'string') throw new Error('Live variant validation mismatch');
        this.live = live;
        this.text('[data-mm-price]', live.money);
        var quantity = this.find('input[name="quantity"]');
        if (quantity) {
          quantity.min = String(live.quantityMin || 1);
          quantity.step = String(live.quantityIncrement || 1);
          if (live.quantityMax != null) quantity.max = String(live.quantityMax); else quantity.removeAttribute('max');
        }
      } catch (error) {
        if (!this.isConnected || epoch !== this.epoch) return;
        this.live = null;
        this.error('Could not verify this design’s price and availability. Please retry.');
        this.hide('[data-mm-retry]', false);
      } finally {
        if (this.isConnected && epoch === this.epoch) { this.loading = false; this.render(); }
      }
    }

    readyVariant() {
      var variant = this.catalog && this.model.exactVariant(this.catalog, this.state);
      return variant && !this.loading && !this.submitting && this.live?.available === true &&
        String(this.live.id) === variant.id ? variant : null;
    }

    render() {
      for (var index = 0; index < 3; index += 1) {
        var group = this.find('[data-mm-step="' + index + '"]');
        if (!group) continue;
        group.disabled = !this.catalog || this.submitting || !this.state.slice(0, index).every(Boolean);
        var allowed = this.catalog ? this.model.allowedValues(this.catalog, this.state, index) : [];
        group.querySelectorAll('input[type="radio"]').forEach(radio => {
          radio.checked = this.state[index] === radio.value;
          radio.disabled = !allowed.includes(radio.value);
          radio.closest('label')?.classList.toggle('is-selected', radio.checked);
          radio.closest('label')?.setAttribute('data-selected', String(radio.checked));
        });
        var optional = this.catalog ? index === this.catalog.options.length - 1 : false;
        this.text('[data-mm-selection="' + index + '"]', this.state[index]
          ? (optional ? this.state[index] + ' — select again to remove' : this.state[index])
          : 'Choose ' + (this.catalog?.options[index].name.toLowerCase() || 'an option'));
      }
      var ready = this.readyVariant();
      var button = this.find('[data-mm-add]');
      if (button) { button.disabled = !ready; button.textContent = this.submitting ? 'Adding…' : this.live?.available === false ? 'Sold out' : 'Add to cart'; }
      var id = this.find('input[name="id"]');
      if (id) { id.value = ready ? ready.id : ''; id.disabled = !ready; }
      var quantity = this.find('input[name="quantity"]');
      if (quantity) quantity.disabled = this.submitting;
      this.setAttribute('aria-busy', String(Boolean(this.loading || this.submitting)));
      var count = this.state.filter(Boolean).length;
      this.text('[data-mm-summary]', count === 3 ? this.state.join(' / ') : 'Choose your pattern, background and frame colour');
      this.text('[data-mm-status]', this.submitting ? 'Adding your design to cart…' : this.loading ? (this.catalog ? 'Checking price and availability…' : 'Loading design options…') :
        this.live?.available === false ? 'This design is sold out. Choose another combination.' : ready ? 'Your design is ready to add to cart.' : count < 3 ? count + ' of 3 choices selected.' : 'Availability must be confirmed before adding to cart.');
    }

    setImage(url, alt) {
      this.hideDigitalPreview();
      var image = this.find('[data-mm-preview-image]');
      if (!image) return;
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = url;
      image.alt = alt;
      image.hidden = false;
    }

    pendingPreview() {
      this.hideDigitalPreview();
      this.hide('[data-mm-preview-image]', true);
      this.text('[data-mm-preview-note]', 'Preview photo pending');
      this.text('[data-mm-preview-title]', this.state.filter(Boolean).join(' / '));
    }

    hideDigitalPreview() {
      this.digitalToken = (this.digitalToken || 0) + 1;
      this.hide('[data-mm-digital-preview]', true);
    }

    renderDigitalPreview() {
      var stage = this.find('[data-mm-digital-preview]');
      var icon = this.safeURL(this.media.patternIcons?.[this.state[0]], true);
      var background = this.media.backgroundSwatches?.[this.state[1]];
      var frame = this.state[2] ? this.media.frameSwatches?.[this.state[2]] : null;
      var hex = value => typeof value === 'string' && /^#(?:[a-f\d]{3}|[a-f\d]{6})$/i.test(value);
      if (!stage || !this.model?.previewKey(this.state) || !icon || !hex(background) || (this.state[2] && !hex(frame))) return false;
      this.hideDigitalPreview();
      var token = this.digitalToken;
      var image = document.createElement('img');
      image.dataset.mmDigitalPattern = '';
      image.alt = '';
      image.width = 800;
      image.height = 800;
      image.addEventListener('error', () => {
        if (this.isConnected && !this.galleryMode && this.digitalToken === token) this.pendingPreview();
      }, { once: true });
      var fullIcon = new URL(icon);
      if (fullIcon.hostname === 'cdn.shopify.com' || fullIcon.origin === window.location.origin) fullIcon.searchParams.set('width', '1200');
      image.src = fullIcon.href;
      stage.replaceChildren(image);
      stage.style.backgroundColor = background;
      stage.style.borderColor = frame || '';
      stage.classList.toggle('has-frame', Boolean(this.state[2]));
      stage.setAttribute('aria-label', this.state.filter(Boolean).join(' / '));
      this.hide('[data-mm-preview-image]', true);
      stage.hidden = false;
      this.text('[data-mm-preview-note]', 'Digital design preview — final texture and colour may vary.');
      this.text('[data-mm-preview-title]', this.state.filter(Boolean).join(' / '));
      return true;
    }

    renderPreview() {
      if (this.galleryMode) return;
      this.hideDigitalPreview();
      this.hide('[data-mm-return-preview]', true);
      var key = this.model?.previewKey(this.state);
      if (!key) {
        var fallback = this.safeURL(this.dataset.defaultImage, true);
        if (fallback) this.setImage(fallback, this.defaultAlt); else this.hide('[data-mm-preview-image]', true);
        this.text('[data-mm-preview-note]', 'Choose a pattern and background to preview your design.');
        this.text('[data-mm-preview-title]', 'Product gallery');
        return;
      }
      var photo = this.safeURL(this.media.previews?.[key], true);
      if (!photo) { if (!this.renderDigitalPreview()) this.pendingPreview(); return; }
      this.setImage(photo, this.state.filter(Boolean).join(' / '));
      this.text('[data-mm-preview-note]', 'Selected design photograph');
      this.text('[data-mm-preview-title]', this.state.filter(Boolean).join(' / '));
    }

    cartURL() {
      // Prefer the configured (locale-aware) cart page; fall back to /cart.
      try {
        var configured = new URL(this.dataset.cartPage, window.location.href);
        if (configured.origin === window.location.origin && /\/cart\/?$/.test(configured.pathname) &&
            !configured.username && !configured.password) return configured.href;
      } catch (_) { /* Fall through to the origin default. */ }
      return new URL('/cart', window.location.origin).href;
    }

    async refreshCartCount() {
      // Repaint the header bubble in place so shoppers can keep designing.
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('section_id', 'cart-icon-bubble');
        var result = await fetch(url.href, { credentials: 'same-origin', signal: this.lifecycle.signal });
        if (!result.ok) return;
        var markup = new DOMParser().parseFromString(await result.text(), 'text/html');
        var next = markup.getElementById('cart-icon-bubble') || markup.body;
        var current = document.getElementById('cart-icon-bubble');
        if (next && current) current.innerHTML = next.innerHTML;
      } catch (_) { /* The count is cosmetic; the cart itself is already correct. */ }
    }

    async addToCart() {
      if (this.submitting) return;
      var variant = this.readyVariant();
      if (!variant) { this.error('Please choose all three options and confirm availability before adding to cart.'); return; }
      var quantity = Number(this.find('input[name="quantity"]')?.value);
      var min = this.live.quantityMin || 1;
      var increment = this.live.quantityIncrement || 1;
      if (!Number.isSafeInteger(quantity) || quantity < min || quantity <= 0 || quantity % increment !== 0 ||
          (this.live.quantityMax != null && quantity > this.live.quantityMax)) {
        this.error('Enter a valid quantity within the allowed minimum, maximum and increments.'); return;
      }
      var url = new URL(this.dataset.cartUrl, window.location.href);
      if (url.origin !== window.location.origin || !/\/cart\/add\.js$/.test(url.pathname) || !Number.isSafeInteger(Number(variant.id))) {
        this.error('The cart could not be verified. Please reload the page.'); return;
      }
      var selected = this.state.slice();
      var design = this.model.designName(selected);
      this.submitting = true;
      this.error('');
      this.hide('[data-mm-success]', true);
      this.render();
      var epoch = this.epoch;
      try {
        var result = await fetch(url.href, { method: 'POST', credentials: 'same-origin', signal: this.lifecycle.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variant.id), quantity, properties: { Design: design } }] }) });
        var body = await result.json();
        if (!this.isConnected || epoch !== this.epoch) return;
        if (!result.ok) throw new Error(typeof body.description === 'string' ? body.description : 'Shopify could not add this design to cart.');
        if (!Array.isArray(body.items) || body.items.length !== 1 || String(body.items[0].variant_id || body.items[0].id) !== variant.id) {
          throw new Error('The cart response could not be confirmed. Please check your cart before trying again.');
        }
        this.text('[data-mm-success-text]', 'Added to cart: ' + design + '.');
        var link = this.find('[data-mm-success] a');
        if (link) link.href = this.cartURL();
        this.hide('[data-mm-success]', false);
        this.refreshCartCount();
      } catch (error) {
        if (this.isConnected && epoch === this.epoch) this.error(error.message || 'Could not add this design. Please check your cart and try again.');
      } finally {
        if (this.isConnected && epoch === this.epoch) { this.submitting = false; this.render(); }
      }
    }
  }
  customElements.define('mm-configurator', MMConfigurator);
}());
